/** React Query hooks for Watchlist Signal Scanning. */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { WatchlistSignal, WatchlistSignalListResponse, ScanResponse } from '../types/watchlistSignals';

// ─── Query params ─────────────────────────────────────────────────

interface SignalQueryParams {
  limit?: number;
  offset?: number;
  signal_type?: string;
  convergence_only?: boolean;
}

// ─── API helpers ──────────────────────────────────────────────────

async function fetchSignals(params: SignalQueryParams): Promise<WatchlistSignalListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.signal_type) qs.set('signal_type', params.signal_type);
  if (params.convergence_only) qs.set('convergence_only', 'true');
  const query = qs.toString();
  return api.request<WatchlistSignalListResponse>(
    `/api/v1/watchlist/signals${query ? `?${query}` : ''}`
  );
}

async function triggerScan(): Promise<ScanResponse> {
  return api.request<ScanResponse>('/api/v1/watchlist/scan', { method: 'POST' });
}

async function actOnSignal(signalId: number): Promise<WatchlistSignal> {
  return api.request<WatchlistSignal>(`/api/v1/watchlist/signals/${signalId}/act`, {
    method: 'POST',
  });
}

async function dismissSignal(signalId: number): Promise<void> {
  return api.request<void>(`/api/v1/watchlist/signals/${signalId}`, { method: 'DELETE' });
}

// ─── Hooks ────────────────────────────────────────────────────────

export function useWatchlistSignals(params: SignalQueryParams = {}) {
  return useQuery({
    queryKey: ['watchlist-signals', params],
    queryFn: () => fetchSignals(params),
    refetchInterval: 60_000,
  });
}

export function useTriggerScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: triggerScan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist-signals'] });
    },
  });
}

export function useActOnSignal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signalId: number) => actOnSignal(signalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist-signals'] });
    },
  });
}

export function useDismissSignal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signalId: number) => dismissSignal(signalId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watchlist-signals'] });
    },
  });
}
